import React, { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions, auth, db } from '../firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';
import '../assets/styles/modal.css';

const WithdrawModal = ({ isOpen, onClose }) => {
    const [method, setMethod] = useState('transfer'); // 'transfer' | 'cash'
    const [formData, setFormData] = useState({
        amount: '',
        bankName: '',
        accountType: '',
        accountNumber: '',
        rut: '',
        email: ''
    });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [userData, setUserData] = useState(null);
    const [isVerified, setIsVerified] = useState(false);

    // Cargar datos del usuario cuando se abre el modal
    useEffect(() => {
        const loadUserData = async () => {
            if (!isOpen || !auth.currentUser) return;
            
            try {
                const userDoc = await getDoc(doc(db, 'usuarios', auth.currentUser.uid));
                if (userDoc.exists()) {
                    const data = userDoc.data();
                    setUserData(data);
                    
                    // Verificar si la cuenta está verificada
                    const verified = data.verificationStatus === 'verified';
                    setIsVerified(verified);
                    
                    // Si está verificado, autocompletar datos bancarios
                    if (verified && data.bankAccount) {
                        setFormData(prev => ({
                            ...prev,
                            bankName: data.bankAccount.bankName || '',
                            accountType: data.bankAccount.accountType || '',
                            accountNumber: data.bankAccount.accountNumber || '',
                            rut: data.bankAccount.rut || '',
                            email: data.email || auth.currentUser.email
                        }));
                    }
                }
            } catch (err) {
                console.error('Error cargando datos del usuario:', err);
            }
        };
        
        loadUserData();
    }, [isOpen]);

    if (!isOpen) return null;

    const handleChange = (e) => {
        // Solo permitir cambios en el monto si el método es transferencia y está verificado
        if (method === 'transfer' && isVerified && e.target.name !== 'amount') {
            return; // No permitir cambios en campos bancarios si está verificado
        }
        setFormData({ ...formData, [e.target.name]: e.target.value });
        setError(''); // Clear error on input change
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');

        try {
            // Verificar que el usuario esté autenticado
            if (!auth.currentUser) {
                throw new Error('Debes iniciar sesión para solicitar un retiro.');
            }

            // Llamar a la Cloud Function
            const solicitarRetiro = httpsCallable(functions, 'solicitarRetiroCLP');
            const result = await solicitarRetiro({
                method,
                amount: formData.amount,
                ...(method === 'transfer' && {
                    bankName: formData.bankName,
                    accountType: formData.accountType,
                    accountNumber: formData.accountNumber,
                    rut: formData.rut
                }),
                email: formData.email || auth.currentUser.email
            });

            if (result.data.success) {
                alert("✅ Solicitud de retiro enviada con éxito.\n\nNuestro equipo procesará tu solicitud en las próximas horas hábiles. Recibirás un correo de confirmación.");
                
                // Reset form
                setFormData({
                    amount: '',
                    bankName: '',
                    accountType: '',
                    accountNumber: '',
                    rut: '',
                    email: ''
                });
                
                onClose();
            }
        } catch (err) {
            console.error('Error al solicitar retiro:', err);
            setError(err.message || 'Error al procesar la solicitud. Intenta nuevamente.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <button className="modal-close" onClick={onClose}>&times;</button>

                <h2 style={{ marginBottom: '20px', textAlign: 'center' }}>Solicitar Retiro</h2>

                {error && (
                    <div style={{ padding: '10px', marginBottom: '15px', background: '#f44336', color: 'white', borderRadius: '5px', fontSize: '0.9rem' }}>
                        {error}
                    </div>
                )}

                <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', justifyContent: 'center' }}>
                    <button
                        type="button"
                        onClick={() => setMethod('transfer')}
                        style={{
                            padding: '8px 15px',
                            borderRadius: '20px',
                            border: 'none',
                            background: method === 'transfer' ? '#4caf50' : '#333',
                            color: 'white',
                            cursor: 'pointer',
                            fontWeight: method === 'transfer' ? 'bold' : 'normal'
                        }}
                    >
                        Transferencia
                    </button>
                    <button
                        type="button"
                        onClick={() => setMethod('cash')}
                        style={{
                            padding: '8px 15px',
                            borderRadius: '20px',
                            border: 'none',
                            background: method === 'cash' ? '#ff9800' : '#333',
                            color: 'white',
                            cursor: 'pointer',
                            fontWeight: method === 'cash' ? 'bold' : 'normal'
                        }}
                    >
                        Efectivo
                    </button>
                </div>

                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>

                    <div className="form-group">
                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.9rem' }}>Monto a Retirar (CLP)</label>
                        <input
                            id="withdraw-amount"
                            type="number"
                            name="amount"
                            value={formData.amount}
                            onChange={handleChange}
                            required
                            style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #444', background: '#222', color: 'white' }}
                            placeholder="Ej: 50000"
                        />
                    </div>

                    {method === 'transfer' ? (
                        <>
                            {isVerified && (
                                <div style={{ background: 'rgba(76,175,80,0.1)', padding: '10px', borderRadius: '5px', borderLeft: '3px solid #4caf50', marginBottom: '15px' }}>
                                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#4caf50' }}>
                                        ✅ <strong>Cuenta Verificada</strong> - Los datos bancarios están bloqueados por seguridad.
                                    </p>
                                </div>
                            )}
                            <div className="form-group">
                                <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.9rem' }}>Banco Destino</label>
                                <input 
                                    id="withdraw-bank-name" 
                                    type="text" 
                                    name="bankName" 
                                    value={formData.bankName} 
                                    onChange={handleChange} 
                                    required
                                    disabled={isVerified}
                                    style={{ 
                                        width: '100%', 
                                        padding: '10px', 
                                        borderRadius: '5px', 
                                        border: '1px solid #444', 
                                        background: isVerified ? '#1a1a1a' : '#222', 
                                        color: isVerified ? '#888' : 'white',
                                        cursor: isVerified ? 'not-allowed' : 'text'
                                    }} 
                                />
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                <div className="form-group">
                                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.9rem' }}>Tipo de Cuenta</label>
                                    <select 
                                        name="accountType" 
                                        value={formData.accountType} 
                                        onChange={handleChange} 
                                        required
                                        disabled={isVerified}
                                        style={{ 
                                            width: '100%', 
                                            padding: '10px', 
                                            borderRadius: '5px', 
                                            border: '1px solid #444', 
                                            background: isVerified ? '#1a1a1a' : '#222', 
                                            color: isVerified ? '#888' : 'white',
                                            cursor: isVerified ? 'not-allowed' : 'pointer'
                                        }}>
                                        <option value="">Seleccionar</option>
                                        <option value="corriente">Cta Corriente</option>
                                        <option value="vista">Cta Vista / RUT</option>
                                        <option value="ahorro">Cta Ahorro</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.9rem' }}>Número Cuenta</label>
                                    <input 
                                        id="withdraw-account-number" 
                                        type="text" 
                                        name="accountNumber" 
                                        value={formData.accountNumber} 
                                        onChange={handleChange} 
                                        required
                                        disabled={isVerified}
                                        style={{ 
                                            width: '100%', 
                                            padding: '10px', 
                                            borderRadius: '5px', 
                                            border: '1px solid #444', 
                                            background: isVerified ? '#1a1a1a' : '#222', 
                                            color: isVerified ? '#888' : 'white',
                                            cursor: isVerified ? 'not-allowed' : 'text'
                                        }} 
                                    />
                                </div>
                            </div>
                            <div className="form-group">
                                <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.9rem' }}>RUT Titular</label>
                                <input 
                                    id="withdraw-rut" 
                                    type="text" 
                                    name="rut" 
                                    value={formData.rut} 
                                    onChange={handleChange} 
                                    required
                                    disabled={isVerified}
                                    style={{ 
                                        width: '100%', 
                                        padding: '10px', 
                                        borderRadius: '5px', 
                                        border: '1px solid #444', 
                                        background: isVerified ? '#1a1a1a' : '#222', 
                                        color: isVerified ? '#888' : 'white',
                                        cursor: isVerified ? 'not-allowed' : 'text'
                                    }} 
                                />
                            </div>
                        </>
                    ) : (
                        <div style={{ background: 'rgba(255,152,0,0.1)', padding: '15px', borderRadius: '5px', borderLeft: '3px solid #ff9800' }}>
                            <p style={{ margin: 0, fontSize: '0.9rem' }}>
                                Has seleccionado retiro en <strong>Efectivo</strong>.
                                <br /><br />
                                Una vez enviada la solicitud, nuestro equipo te contactará para coordinar la entrega segura en nuestras oficinas o punto autorizado.
                            </p>
                        </div>
                    )}

                    <div className="modal-actions" style={{ marginTop: '10px' }}>
                        <button type="submit" className="btn btn-primary" disabled={isLoading} style={{ width: '100%' }}>
                            {isLoading ? 'Procesando...' : 'Confirmar Solicitud'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default WithdrawModal;
